<template>
  <div class="invoice-form">
    <b-row>
      <b-col lg="6" md="6" sm="12">
        <legend>
          <strong>{{ $t('invoiceDetails') }}</strong>
        </legend>

        <b-form-group
          horizontal
          :label="$t('invoiceType')"
          label-for="invoiceType"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :placeholder="$t('invoiceType') + ' ' + $t('select')"
            v-model="localInvoice.invoiceType"
            :options="getEnumValues('InvoiceType')"
            :show-labels="true"
            :allow-empty="false"
            :taggable="true"
            :selectLabel="$t('select')"
            :selectedLabel="$t('selected')"
            :deselectLabel="$t('remove_selection')"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <b-badge :variant="getInvoiceTypeColor(option)">{{ $t(option) }}</b-badge>
            </template>
            <template slot="option" slot-scope="{ option }">
              <b-badge :variant="getInvoiceTypeColor(option)">
                {{ $t(option) }}
              </b-badge>
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('invoiceState')"
          label-for="invoiceState"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :placeholder="$t('invoiceState') + ' ' + $t('select')"
            v-model="localInvoice.invoiceState"
            :options="getEnumValues('InvoiceState')"
            :show-labels="true"
            :allow-empty="false"
            :taggable="true"
            :selectLabel="$t('select')"
            :selectedLabel="$t('selected')"
            :deselectLabel="$t('remove_selection')"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <b-badge :variant="getInvoiceStateColor(option)">{{ $t(option) }}</b-badge>
            </template>
            <template slot="option" slot-scope="{ option }">
              <b-badge :variant="getInvoiceStateColor(option)">
                {{ $t(option) }}
              </b-badge>
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('invoiceNumber')"
          label-for="invoiceNumber"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input
            v-model="localInvoice.invoiceNumber"
            type="text"
            :placeholder="$t('invoiceNumber')"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('invoiceDate')"
          label-for="invoiceDate"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <Calendar
            v-model="invoiceDate"
            selectionMode="single"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date')"
            :showWeek="true"
            :yearNavigator="true"
            :monthNavigator="true"
            yearRange="2020:2030"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('dueDate')"
          label-for="dueDate"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <Calendar
            v-model="dueDate"
            selectionMode="single"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date')"
            :showWeek="true"
            :yearNavigator="true"
            :monthNavigator="true"
            yearRange="2020:2030"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('paidDate')"
          label-for="paidDate"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <Calendar
            v-model="paidDate"
            selectionMode="single"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date')"
            :showWeek="true"
            :yearNavigator="true"
            :monthNavigator="true"
            yearRange="2020:2030"
          />
        </b-form-group>
      </b-col>

      <b-col lg="6" md="6" sm="12">
        <legend>
          <strong>{{ $t('amounts') }}</strong>
        </legend>

        <b-form-group
          horizontal
          :label="$t('netAmount')"
          label-for="netAmount"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input
            id="netAmount"
            v-model.number="localInvoice.netAmount"
            type="number"
            step="0.01"
            :placeholder="$t('netAmount')"
            @input="applyTaxCalculation"
          />
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('taxAmount')"
          label-for="taxCalculationMode"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <SelectButton
            id="taxCalculationMode"
            v-model="taxCalculationMode"
            class="calculation-mode-select"
            :options="automaticManualOptions"
          >
            <template #option="props">
              {{ $t(props.option) }}
            </template>
          </SelectButton>

          <Dropdown
            v-if="isTaxCalculationAutomatic"
            v-model="localInvoice.vatPercentage"
            class="mt-2"
            :options="vatPercentageOptions"
            :placeholder="$t('vatPercentageState')"
            optionLabel="label"
            optionValue="value"
            @change="applyTaxCalculation"
          />

          <b-form-input
            v-if="!isTaxCalculationAutomatic"
            id="taxAmount"
            class="mt-2"
            v-model.number="localInvoice.taxAmount"
            type="number"
            step="0.01"
            :placeholder="$t('taxAmount')"
            @input="applyTaxCalculation"
          />
          <div v-else class="form-control-plaintext">
            {{ formatNumber(localInvoice.taxAmount, NUMBER_FORMAT.CURRENCY) }}
          </div>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('grossAmount')"
          label-for="grossAmount"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <div id="grossAmount" class="form-control-plaintext">
            {{ formatNumber(localInvoice.grossAmount, NUMBER_FORMAT.CURRENCY) }}
          </div>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="6" md="6" sm="12">
        <legend>
          <strong>{{ $t('references') }}</strong>
        </legend>

        <b-form-group
          horizontal
          :label="$t('supplier')"
          label-for="supplier"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            class="multiselect-supplier"
            :placeholder="$t('supplier') + ' ' + $t('select') + '/suchen'"
            v-model="localInvoice.supplier"
            :options="filteredSuppliers"
            track-by="_id"
            :allow-empty="true"
            :deselectLabel="$t('deselectLabel')"
            selectLabel=""
            selectedLabel=""
            :internal-search="false"
            :close-on-select="true"
            :clear-on-select="false"
            :multiple="false"
            :show-labels="true"
            @search-change="onSupplierSearch"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <div v-if="option">
                {{ option.name }}
              </div>
            </template>
            <template slot="option" slot-scope="{ option }">
              <div>{{ option.name }}</div>
            </template>
            <template slot="noResult">
              <div>{{ $t('no_results') }}</div>
            </template>
          </multiselect>
        </b-form-group>

        <b-form-group
          v-if="!projectContext"
          horizontal
          :label="$t('project')"
          label-for="project"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            class="multiselect-project"
            :placeholder="$t('filter_by_lastname')"
            v-model="localInvoice.project"
            :options="filteredProjects"
            :allow-empty="true"
            track-by="number"
            :deselectLabel="$t('deselectLabel')"
            selectLabel=""
            selectedLabel=""
            :internal-search="false"
            :show-labels="true"
            @search-change="onProjectSearch"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              <div>
                #{{ option.number.toString().padStart(4, '0') }} - {{ option.customer.lastname }},
                {{ option.customer.firstname }}
              </div>
              <div>
                {{ option.customer.street }} {{ option.customer.streetNumber
                }}<span v-if="option.customer.streetNumberSuffix">{{
                  option.customer.streetNumberSuffix
                }}</span
                >, {{ option.customer.zip }}
                {{ option.customer.city }}
              </div>
            </template>
            <template slot="option" slot-scope="{ option }">
              <div>
                #{{ option.number.toString().padStart(4, '0') }}: {{ option.customer.lastname }},
                {{ option.customer.firstname }}
              </div>
              <div>
                {{ option.customer.street }} {{ option.customer.streetNumber }},
                {{ option.customer.streetNumberSuffix }}, {{ option.customer.zip }}
                {{ option.customer.city }}
              </div>
            </template>
          </multiselect>
        </b-form-group>
      </b-col>

      <b-col lg="6" md="6" sm="12">
        <legend>
          <strong>{{ $t('additionalInfo') }}</strong>
        </legend>

        <b-form-group
          horizontal
          :label="$t('notes')"
          label-for="notes"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-textarea v-model="localInvoice.notes" :placeholder="$t('notes')" rows="3" />
        </b-form-group>

        <b-form-group
          v-if="localInvoice.number && localInvoice.number !== '_new'"
          horizontal
          :label="$t('number')"
          label-for="number"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
          class="mb-0"
        >
          <div class="form-control-plaintext">
            {{ localInvoice.number.toString().padStart(4, '0') }}
          </div>
        </b-form-group>

        <b-form-group
          v-if="localInvoice.createdBy"
          horizontal
          :label="$t('createdBy')"
          label-for="createdBy"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
          class="mb-0"
        >
          <div class="form-control-plaintext">
            {{ localInvoice.createdBy.firstname }} {{ localInvoice.createdBy.lastname
            }}<span v-if="localInvoice.createdAt"
              >, {{ $dayjs(localInvoice.createdAt).format('DD.MM.YYYY HH:mm') }}</span
            >
          </div>
        </b-form-group>

        <b-form-group
          v-if="localInvoice.updatedBy"
          horizontal
          :label="$t('updatedBy')"
          label-for="updatedBy"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
          class="mb-0"
        >
          <div class="form-control-plaintext">
            {{ localInvoice.updatedBy.firstname }} {{ localInvoice.updatedBy.lastname
            }}<span v-if="localInvoice.updatedAt"
              >, {{ $dayjs(localInvoice.updatedAt).format('DD.MM.YYYY HH:mm') }}</span
            >
          </div>
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Attachments Section -->
    <b-row>
      <b-col lg="6" md="6" sm="12">
        <div class="attachments-section mt-4">
          <legend>
            <strong>{{ $t('attachments') }}</strong>
          </legend>

          <div v-if="localInvoice._id && localInvoice._id !== '_new'">
            <b-form-group
              horizontal
              :label="$t('attachmentType')"
              label-for="attachmentType"
              label-class="text-md-right"
              :label-cols="4"
              breakpoint="md"
            >
              <multiselect
                :placeholder="$t('attachmentType')"
                v-model="selectedAttachmentType"
                id="attachmentType"
                :options="attachmentTypes"
                :allow-empty="false"
                :show-labels="false"
              >
                <template slot="singleLabel" slot-scope="{ option }">
                  {{ $t(option) }}
                </template>
                <template slot="option" slot-scope="{ option }">
                  {{ $t(option) }}
                </template>
              </multiselect>

              <DropzoneUpload
                class="mt-2"
                :complete="isAttachmentUploadComplete"
                :parentId="localInvoice._id"
                :parentType="selectedAttachmentType"
                :uploadCallback="uploadInvoiceAttachment"
                :disabled="isUploadDisabled"
                v-on:dropzone-upload:reset="isAttachmentUploadComplete = false"
              />
            </b-form-group>
          </div>

          <p v-else class="text-muted">
            {{ $t('save_before_upload') }}
          </p>

          <!-- Attachment Lists by Type -->
          <div v-for="attachmentType in attachmentTypes" :key="attachmentType">
            <legend v-if="getAttachmentsByType(attachmentType).length > 0">
              <strong>{{ $t(attachmentType) }}</strong>
            </legend>
            <AttachmentList
              :attachments="getAttachmentsByType(attachmentType)"
              :attachmentType="attachmentType"
              v-on:attachment-list:delete="onDeleteAttachment"
            />
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Form Actions (when in project context) -->
    <b-row v-if="projectContext">
      <b-col lg="12" md="12" sm="12">
        <div class="form-actions d-flex justify-content-end mt-3">
          <button v-if="showCancelButton" class="btn btn-default mr-2" @click="$emit('cancel')">
            {{ $t('cancel') }}
          </button>
          <button class="btn btn-inverse" @click="handleSave" :disabled="!isFormValid">
            {{ localInvoice.number === '_new' ? $t('create') : $t('save') }}
          </button>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Multiselect from 'vue-multiselect';
import Calendar from 'primevue/calendar';
import SelectButton from 'primevue/selectbutton';
import Dropdown from 'primevue/dropdown';
import { getInvoiceStateColor, getInvoiceTypeColor } from '@/helpers/colors';
import { formatNumber, NUMBER_FORMAT } from '@/helpers/numberFormat';
import { FilterMatchMode } from 'primevue/api';
import AttachmentList from '@/components/AttachmentList.vue';
import DropzoneUpload from '@/components/DropzoneUpload.vue';

const MODE_AUTOMATIC = 'AUTOMATIC';
const MODE_MANUAL = 'MANUAL';
const DEFAULT_VAT_PERCENTAGE = 19;

export default {
  name: 'InvoiceForm',
  components: {
    Multiselect,
    Calendar,
    SelectButton,
    Dropdown,
    AttachmentList,
    DropzoneUpload,
  },
  props: {
    invoice: {
      type: Object,
      required: true,
    },
    projectContext: {
      type: Object,
      default: null,
    },
    showCancelButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localInvoice: { ...this.invoice },
      filteredProjects: [],
      filteredSuppliers: [],
      isAttachmentUploadComplete: false,
      selectedAttachmentType: 'INVOICE_DOCUMENT',
      NUMBER_FORMAT,
      automaticManualOptions: [MODE_AUTOMATIC, MODE_MANUAL],
      vatPercentageOptions: [
        { value: 19, label: '19 %' },
        { value: 7, label: '7 %' },
        { value: 0, label: '0 %' },
      ],
      projectsFilter: {
        pagination: {
          page: 0,
          rowsPerPage: 50,
        },
        sortField: 'number',
        sortOrder: -1,
        filterName: 'invoices-project-filters',
        filters: {
          number: { value: null, matchMode: FilterMatchMode.EQUALS },
          customerLastname: { value: null, matchMode: FilterMatchMode.CONTAINS },
          customerFirstname: { value: null, matchMode: FilterMatchMode.CONTAINS },
          projectCustomerSearch: { value: null },
        },
      },
      suppliersFilter: {
        pagination: {
          skip: 0,
          pageSize: 20,
        },
        sorting: {
          sortField: 'number',
          sortOrder: 1,
        },
        filterName: 'invoices-supplier-filters',
        filters: {
          number: { value: null, matchMode: FilterMatchMode.EQUALS },
          name: { value: null, matchMode: FilterMatchMode.CONTAINS },
        },
      },
    };
  },
  computed: {
    ...mapGetters(['getEnumValues', 'getProjects', 'getSuppliers']),
    ...mapGetters('auth', ['isAdmin', 'isClient', 'getCurrentUser']),

    invoiceDate: {
      get() {
        return this.localInvoice.invoiceDate
          ? this.$dayjs(this.localInvoice.invoiceDate).toDate()
          : null;
      },
      set(value) {
        this.localInvoice.invoiceDate = value;
        this.emitUpdate();
      },
    },

    dueDate: {
      get() {
        return this.localInvoice.dueDate ? this.$dayjs(this.localInvoice.dueDate).toDate() : null;
      },
      set(value) {
        this.localInvoice.dueDate = value;
        this.emitUpdate();
      },
    },

    paidDate: {
      get() {
        return this.localInvoice.paidDate ? this.$dayjs(this.localInvoice.paidDate).toDate() : null;
      },
      set(value) {
        this.localInvoice.paidDate = value;
        this.emitUpdate();
      },
    },

    taxCalculationMode: {
      get() {
        return this.localInvoice.vatPercentage != null ? MODE_AUTOMATIC : MODE_MANUAL;
      },
      set(mode) {
        if (mode === MODE_AUTOMATIC) {
          if (this.localInvoice.vatPercentage == null) {
            this.localInvoice.vatPercentage = DEFAULT_VAT_PERCENTAGE;
          }
        } else {
          this.localInvoice.vatPercentage = null;
        }
        this.applyTaxCalculation();
      },
    },

    isTaxCalculationAutomatic() {
      return this.taxCalculationMode === MODE_AUTOMATIC;
    },

    isFormValid() {
      return true;
    },

    isNew() {
      return this.localInvoice.number === '_new';
    },

    attachmentTypes() {
      // Invoice attachment types - can be extended later
      return ['INVOICE_DOCUMENT'];
    },

    isUploadDisabled() {
      return this.selectedAttachmentType === null;
    },
  },
  watch: {
    invoice: {
      handler(newInvoice) {
        console.log('InvoiceForm: invoice changed', newInvoice);
        this.localInvoice = { ...newInvoice };
        this.seedGrossOnlyLegacyAmounts();
      },
      deep: true,
      immediate: true,
    },
    localInvoice: {
      handler() {
        this.emitUpdate();
      },
      deep: true,
    },
    projectContext: {
      handler(newProject) {
        if (newProject) {
          this.localInvoice.project = newProject;
          this.emitUpdate();
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions([
      'fetchProjectsPaginated',
      'fetchProjectsPaginatedByClient',
      'fetchSuppliersPaginated',
      'uploadInvoiceAttachment',
      'deleteInvoiceAttachment',
      'fetchEnumValues',
    ]),

    getInvoiceStateColor,
    getInvoiceTypeColor,
    formatNumber,

    applyTaxCalculation() {
      if (this.seedGrossOnlyLegacyAmounts()) {
        return;
      }

      const netAmount = this.localInvoice.netAmount ?? 0;

      if (this.localInvoice.vatPercentage != null) {
        const taxAmount = this.roundMoney((netAmount * this.localInvoice.vatPercentage) / 100);
        this.localInvoice.taxAmount = taxAmount;
        this.localInvoice.grossAmount = this.roundMoney(netAmount + taxAmount);
        return;
      }

      const taxAmount = this.localInvoice.taxAmount ?? 0;
      this.localInvoice.grossAmount = this.roundMoney(netAmount + taxAmount);
    },

    seedGrossOnlyLegacyAmounts() {
      if (!this.isGrossOnlyLegacyInvoice(this.localInvoice)) {
        return false;
      }

      this.localInvoice.netAmount = this.localInvoice.grossAmount;
      this.localInvoice.taxAmount = 0;
      return true;
    },

    isGrossOnlyLegacyInvoice(invoice) {
      if (invoice.vatPercentage != null) {
        return false;
      }

      const netAmount = invoice.netAmount ?? 0;
      const taxAmount = invoice.taxAmount ?? 0;
      const grossAmount = invoice.grossAmount ?? 0;

      return netAmount === 0 && taxAmount === 0 && grossAmount > 0;
    },

    roundMoney(value) {
      if (!Number.isFinite(value)) {
        return 0;
      }
      const factor = 100;
      return Math.round((value + Number.EPSILON) * factor) / factor;
    },

    emitUpdate() {
      this.$emit('update:invoice', this.localInvoice);
    },

    handleSave() {
      this.$emit('save', this.localInvoice);
    },

    async onProjectSearch(rawQuery) {
      const query = rawQuery.toLowerCase();
      this.projectsFilter.filters.projectCustomerSearch.value = query;
      await this.loadProjectData();
    },

    async onSupplierSearch(rawQuery) {
      const query = rawQuery.toLowerCase();
      this.suppliersFilter.filters.name.value = query;
      await this.loadSupplierData();
    },

    async loadProjectData() {
      if (this.isClient) {
        await this.fetchProjectsPaginatedByClient({
          clientId: this.getCurrentUser.client.id,
          page: this.projectsFilter.pagination.page,
          pageSize: this.projectsFilter.pagination.rowsPerPage,
          sortField: this.projectsFilter.sortField,
          sortOrder: this.projectsFilter.sortOrder,
          filters: { ...this.projectsFilter.filters },
        });
      } else if (this.isAdmin) {
        await this.fetchProjectsPaginated({
          page: this.projectsFilter.pagination.page,
          pageSize: this.projectsFilter.pagination.rowsPerPage,
          sortField: this.projectsFilter.sortField,
          sortOrder: this.projectsFilter.sortOrder,
          filters: { ...this.projectsFilter.filters },
        });
      }
      this.filteredProjects = this.getProjects;

      // Ensure current project is in the list
      if (this.localInvoice && this.localInvoice.project) {
        const projectExists = this.filteredProjects.find(
          (p) => p.number === this.localInvoice.project.number
        );
        if (!projectExists) {
          this.filteredProjects.unshift(this.localInvoice.project);
        }
      }
    },

    async loadSupplierData() {
      await this.fetchSuppliersPaginated({
        pagination: this.suppliersFilter.pagination,
        sorting: this.suppliersFilter.sorting,
        filters: { ...this.suppliersFilter.filters },
      });
      this.filteredSuppliers = this.getSuppliers;

      // Ensure current supplier is in the list
      if (this.localInvoice && this.localInvoice.supplier) {
        const supplierExists = this.filteredSuppliers.find(
          (s) => s._id === this.localInvoice.supplier._id
        );
        if (!supplierExists) {
          this.filteredSuppliers.unshift(this.localInvoice.supplier);
        }
      }
    },

    async onDeleteAttachment(event) {
      await this.deleteInvoiceAttachment({
        invoiceId: this.localInvoice._id,
        attachmentId: event.attachmentId,
      });
    },

    getAttachmentsByType(attachmentType) {
      if (!this.localInvoice.attachments) return [];
      return this.localInvoice.attachments.filter(
        (attachment) => attachment.attachmentType === attachmentType
      );
    },
  },

  async mounted() {
    await this.fetchEnumValues('InvoiceType');
    await this.fetchEnumValues('InvoiceState');
    if (!this.projectContext) {
      await this.loadProjectData();
    }
    await this.loadSupplierData();

    // After upload is complete emit event to clear dropzone preview
    this.$store.subscribeAction({
      after: (action, state) => {
        if (action.type === 'uploadInvoiceAttachment') {
          this.isAttachmentUploadComplete = true;
        }
      },
    });
  },
};
</script>

<style scoped lang="scss">
.p-calendar {
  width: 100%;
}

.attachments-section {
  border-top: 1px solid #dee2e6;
  padding-top: 1rem;
}

.form-actions {
  border-top: 1px solid #dee2e6;
  padding-top: 1rem;
  margin-top: 1rem;
}

::v-deep .calculation-mode-select.p-selectbutton .p-button {
  padding: 0.1rem 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.875rem;
}
</style>
